import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import { requireContractProductAtLeast } from '../../../components/Misc/RestrictedProduct'
import { ContractProduct } from '../../../types/contract/Contract'
import { FiscalYear, Period } from '../../../types/fiscalYear/FiscalYear'
import { PeriodGroup } from '../../../types/periodGroup/PeriodGroup'
import { contextContractSelector } from '../../../redux/context/contract/selectors'
import { filtersSelector, forecastCursorSelector } from '../../../redux/context/filters/selectors'
import { periodGroupLabel, periodLabel } from '../../../redux/context/fiscalYears/utils'
import { columnPeriodsSelector } from '../../../redux/context/periodGroups/selectors'
import { useCtxReportablePeriodGroups } from '../../fiscalYear/hooks/useCtxReportablePeriodGroups'
import { useSelectedFiscalYears } from '../../fiscalYear/hooks/useSelectedFiscalYears'
import { filterGroupsBySelectedFiscalYears } from '../../fiscalYear/utils'
import { StatementColumn, StatementDataType } from '../types'
import { ReportDataType } from '../../../redux/context/reports/types'
import { useCtxFiscalYears } from '../../fiscalYear/queries/report/useCtxFiscalYears'
import { createDataKey } from '../../../shared/utils/balanceRegistryUtils'

const REPORT_TO_STATEMENT_DATA_TYPE: Record<ReportDataType, StatementDataType | undefined> = {
  [ReportDataType.actuals]: StatementDataType.actuals,
  [ReportDataType.budget]: StatementDataType.budget,
  [ReportDataType.forecast]: StatementDataType.forecast,
  [ReportDataType.comparativePeriod]: StatementDataType.actuals // Comparative period is actuals from previous year
}

const mapReportDataTypeToStatementDataType = (reportDataType: ReportDataType): StatementDataType | undefined => {
  return REPORT_TO_STATEMENT_DATA_TYPE[reportDataType]
}

const getColumnClassName = (period: Period, dataType: ReportDataType, forecastCursor?: '' | dayjs.Dayjs | null) => {
  // forecastCursor affects only forecast columns
  if (
    dataType === ReportDataType.forecast &&
    forecastCursor &&
    (period.year > forecastCursor.year() ||
      (period.year === forecastCursor.year() && period.month > forecastCursor.month() + 1))
  ) {
    return `${dataType}-column-bright`
  }
  return `${dataType}-column`
}

const createPeriodColumn = (
  period: Period,
  columnType: ReportDataType,
  forecastCursor?: '' | dayjs.Dayjs | null,
  fiscalYear?: FiscalYear
): StatementColumn => {
  // For comparative period, use previous year
  const isComparative = columnType === ReportDataType.comparativePeriod
  const year = isComparative ? period.year - 1 : period.year
  const elementKey = createDataKey(year, period.month)

  const dataType = mapReportDataTypeToStatementDataType(columnType)
  if (!dataType) {
    throw new Error(`Invalid report data type: ${columnType}`)
  }

  return {
    dataSource: 'elements',
    fiscalYear,
    label: periodLabel(period, columnType),
    year,
    month: period.month,
    dataType,
    columnType,
    className: getColumnClassName(period, columnType, forecastCursor),
    dataKey: elementKey
  }
}

const createGroupColumn = (group: FiscalYear | PeriodGroup, columnType: ReportDataType): StatementColumn => {
  // Check if group is a FiscalYear by checking for unique 'year' property
  const fiscalYear = 'year' in group ? (group as FiscalYear) : undefined

  const dataType = mapReportDataTypeToStatementDataType(columnType)
  if (!dataType) {
    throw new Error(`Invalid report data type: ${columnType}`)
  }

  return {
    dataSource: 'groups',
    fiscalYear,
    label: periodGroupLabel(group, columnType),
    dataType,
    columnType,
    className: `${columnType}-column`,
    periodGroup: group,
    dataKey: group.id
  }
}

const filterColumnTypes = (
  columnTypes: ReportDataType[],
  options: {
    budgetCategoriesEnabled: boolean
    group: FiscalYear | PeriodGroup
    filterFutureActuals?: boolean
    filterComparativePeriod?: boolean
  }
): ReportDataType[] => {
  const { budgetCategoriesEnabled, group, filterFutureActuals, filterComparativePeriod } = options

  const isBudgetableDisabled = !budgetCategoriesEnabled || ('budgetable' in group && !group?.budgetable)
  const isFuturePeriod = dayjs(group.startDate) > dayjs()

  return columnTypes.filter(dataType => {
    // Filter out comparative period if requested
    if (filterComparativePeriod && dataType === ReportDataType.comparativePeriod) {
      return false
    }

    // Filter out budget & forecast if budgeting is disabled
    if (isBudgetableDisabled && (dataType === ReportDataType.budget || dataType === ReportDataType.forecast)) {
      return false
    }

    // Filter out actuals for future periods if requested
    if (filterFutureActuals && isFuturePeriod && dataType === ReportDataType.actuals) {
      return false
    }

    return true
  })
}

export const useStatementTableColumns = (): StatementColumn[] => {
  const {
    dataTypes: columnTypes,
    periodGroup: periodGroupFilter,
    groupSortOrder = 'asc',
    monthSortOrder = 'asc'
  } = useSelector(filtersSelector)
  const contract = useSelector(contextContractSelector)
  const budgetCategoriesEnabled = !!(
    contract?.product && requireContractProductAtLeast(contract.product, ContractProduct.BASIC)
  )
  const periods = useSelector(columnPeriodsSelector)
  const groups = useCtxReportablePeriodGroups()
  const { data: fiscalYears } = useCtxFiscalYears()
  const { selected: selectedFiscalYears } = useSelectedFiscalYears()

  // Use selector that memoizes forecastCursor as dayjs object
  const forecastCursor = useSelector(forecastCursorSelector)

  const tableColumns = useMemo((): StatementColumn[] => {
    if (periodGroupFilter && periods) {
      // Period-based columns
      const columns: StatementColumn[] = []
      // Check if periodGroupFilter is a FiscalYear by checking for unique 'year' property
      const fiscalYear = 'year' in periodGroupFilter ? (periodGroupFilter as FiscalYear) : undefined
      const filteredColumnTypes = filterColumnTypes(columnTypes, {
        budgetCategoriesEnabled,
        group: periodGroupFilter,
        filterFutureActuals: true
      })
      const orderedPeriods = monthSortOrder === 'desc' ? periods.slice().reverse() : periods
      for (const period of orderedPeriods) {
        for (const columnType of filteredColumnTypes) {
          // For comparative period, find the correct fiscalYear from previous year
          let columnFiscalYear = fiscalYear
          if (columnType === ReportDataType.comparativePeriod && period.year) {
            const comparativeYear = period.year - 1
            columnFiscalYear = fiscalYears?.find(fy => fy.year === comparativeYear)
          }
          columns.push(createPeriodColumn(period, columnType, forecastCursor, columnFiscalYear))
        }
      }

      // console.log('Period columns', columns)

      return columns
    }

    // Group-based columns
    const columns: StatementColumn[] = []

    // Groups come in ascending order; only the shown columns and their display order change here
    const shownGroups = filterGroupsBySelectedFiscalYears(groups, selectedFiscalYears)
    const orderedGroups = groupSortOrder === 'desc' ? shownGroups.slice().reverse() : shownGroups
    for (const group of orderedGroups) {
      if (group.id > 0) {
        const filteredColumnTypes = filterColumnTypes(columnTypes, {
          budgetCategoriesEnabled,
          group,
          filterFutureActuals: true,
          filterComparativePeriod: true
        })
        for (const columnType of filteredColumnTypes) {
          columns.push(createGroupColumn(group, columnType))
        }
      }
    }

    // console.log('Group columns', columns)

    return columns
  }, [
    periodGroupFilter,
    periods,
    groups,
    forecastCursor,
    columnTypes,
    budgetCategoriesEnabled,
    fiscalYears,
    groupSortOrder,
    monthSortOrder,
    selectedFiscalYears
  ])

  return tableColumns
}
